import React, { useMemo, useState } from 'react';
import {
  Box, Flex, Grid, Text, Select, Textarea, Button, Alert, AlertIcon, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
} from '@chakra-ui/react';
import { STUDENT_SCHOOL_GROUPS } from 'utils/studentSchools';
import {
  parseSheetPaste, suggestSchool, previewImport, applyImport, schoolLabel, errorMessage,
} from 'api/administrador/schoolAssignment';

/**
 * Importar el colegio desde el Excel del área (pedido de Andrea, 26 oct 2026). Se pega una pestaña
 * completa; se toman «Codigo» y «Colegio» (y «Numero», si está, para detectar códigos mal digitados).
 * Pasos: pegar → elegir a qué colegio corresponde cada nombre del Excel → vista previa → aplicar.
 */

const SKIP = '__omitir';
const STATUS = {
  cambia: { label: 'Cambia', tone: 'purple' },
  igual: { label: 'Ya está bien', tone: 'green' },
  revisar: { label: 'Revisar', tone: 'orange' },
  duplicado: { label: 'Repetido', tone: 'orange' },
  no_encontrado: { label: 'No encontrado', tone: 'red' },
};
const SHOW = 200;

function SchoolSelect({ value, onChange, label }) {
  return (
    <Select aria-label={label} size="sm" borderRadius="8px" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Elegir colegio…</option>
      {STUDENT_SCHOOL_GROUPS.map((g) => (
        <optgroup key={g.label} label={g.label}>
          {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </optgroup>
      ))}
      <option value={SKIP}>No importar estas filas</option>
    </Select>
  );
}

export default function ImportExcel({ isOpen, onClose, onDone }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [tab, setTab] = useState('cambia');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setText(''); setParsed(null); setMapping({}); setPreview(null); setTab('cambia'); setError('');
  };
  const close = () => { if (!busy) { reset(); onClose(); } };

  // Nombres distintos del Excel, con cuántas filas tiene cada uno.
  const names = useMemo(() => {
    const m = new Map();
    (parsed?.rows || []).forEach((r) => m.set(r.name, (m.get(r.name) || 0) + 1));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [parsed]);

  const read = () => {
    const res = parseSheetPaste(text);
    if (res.error) { setError(res.error); return; }
    setError('');
    setParsed(res);
    const m = {};
    res.rows.forEach((r) => { if (!(r.name in m)) m[r.name] = suggestSchool(r.name); });
    setMapping(m);
  };

  const unmapped = names.filter(([n]) => !mapping[n]);
  const rowsToSend = () => (parsed?.rows || [])
    .filter((r) => mapping[r.name] && mapping[r.name] !== SKIP)
    .map((r) => ({ line: r.line, code: r.code, school: mapping[r.name], document: r.document }));

  const runPreview = async () => {
    const rows = rowsToSend();
    if (!rows.length) { setError('No hay filas para importar con los colegios elegidos.'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await previewImport(rows);
      setPreview(res);
      setTab(res.summary.cambia ? 'cambia' : Object.keys(STATUS).find((k) => res.summary[k]) || 'cambia');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await applyImport(rowsToSend(), preview.summary.cambia);
      toast({
        status: 'success',
        title: `Se actualizó el colegio de ${res.updated.toLocaleString('es-CO')} ${res.updated === 1 ? 'estudiante' : 'estudiantes'}`,
        duration: 6000,
        isClosable: true,
      });
      reset();
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const items = (preview?.items || []).filter((i) => i.status === tab);

  return (
    <Modal isOpen={isOpen} onClose={close} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="16px">
        <ModalHeader fontSize="18px">Importar colegios desde Excel</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          {error && (
            <Alert status="error" borderRadius="8px" mb={4} fontSize="sm"><AlertIcon />{error}</Alert>
          )}

          {!parsed && (
            <>
              <Text fontSize="sm" color="gray.600" mb={3}>
                En el Excel, selecciona la pestaña completa incluyendo la fila de encabezados (Ctrl+A y Ctrl+C) y pégala aquí.
                Se usan las columnas <b>Codigo</b> y <b>Colegio</b>; si está la columna <b>Numero</b> (documento), se compara con
                la plataforma para evitar errores por códigos mal digitados.
              </Text>
              <Textarea
                aria-label="Datos pegados del Excel" value={text} onChange={(e) => setText(e.target.value)}
                placeholder="Pega aquí la pestaña del Excel…" rows={10} fontFamily="mono" fontSize="12px" borderRadius="8px"
              />
            </>
          )}

          {parsed && !preview && (
            <>
              <Text fontSize="sm" color="gray.600" mb={1}>
                Se leyeron <b>{parsed.rows.length.toLocaleString('es-CO')}</b> filas con código y colegio
                {parsed.skippedNoSchool ? ` (${parsed.skippedNoSchool} sin colegio en el Excel no se tienen en cuenta)` : ''}.
                {!parsed.hasDocument && ' No se encontró la columna «Numero»: no se podrá comparar el documento.'}
              </Text>
              <Text fontSize="sm" color="gray.600" mb={4}>Elige a qué colegio de la plataforma corresponde cada nombre del Excel:</Text>
              <Grid templateColumns={{ base: '1fr', md: '1fr 320px' }} gap={3} alignItems="center">
                {names.map(([name, n]) => (
                  <React.Fragment key={name}>
                    <Box>
                      <Text fontSize="sm" fontWeight="600">{name}</Text>
                      <Text fontSize="12px" color="gray.500">
                        {n.toLocaleString('es-CO')} {n === 1 ? 'fila' : 'filas'}
                        {!suggestSchool(name) && /iberoamericano/i.test(name) && ' · puede ser Anual o CLEI: elige según el programa de esta pestaña'}
                      </Text>
                    </Box>
                    <SchoolSelect label={`Colegio para ${name}`} value={mapping[name] || ''} onChange={(v) => setMapping((m) => ({ ...m, [name]: v }))} />
                  </React.Fragment>
                ))}
              </Grid>
            </>
          )}

          {preview && (
            <>
              <Flex gap={2} wrap="wrap" mb={4}>
                {Object.entries(STATUS).map(([key, s]) => (
                  <Button
                    key={key} size="sm" borderRadius="full" variant={tab === key ? 'solid' : 'outline'}
                    colorScheme={tab === key ? 'purple' : 'gray'} onClick={() => setTab(key)}
                  >
                    {s.label} · {preview.summary[key].toLocaleString('es-CO')}
                  </Button>
                ))}
              </Flex>
              {tab === 'revisar' && preview.summary.revisar > 0 && (
                <Alert status="warning" borderRadius="8px" mb={3} fontSize="sm">
                  <AlertIcon />
                  El documento del Excel no coincide con el de la plataforma para ese código. No se cambian: revisa el código en el Excel.
                </Alert>
              )}
              {items.length === 0 && <Text fontSize="sm" color="gray.500" py={4}>No hay filas en este grupo.</Text>}
              {items.slice(0, SHOW).map((i) => (
                <Grid key={`${i.line}-${i.code}`} templateColumns="70px 1.4fr 2fr" gap={3} py={2} borderTop="1px solid" borderColor="gray.100" alignItems="center">
                  <Text fontSize="12px" color="gray.500" fontFamily="mono">{i.code || '—'}</Text>
                  <Box minW={0}>
                    <Text fontSize="sm" fontWeight="600" isTruncated>{i.name || `Fila ${i.line}`}</Text>
                    {i.identity && <Text fontSize="12px" color="gray.500" fontFamily="mono">{i.identity}</Text>}
                  </Box>
                  <Box minW={0}>
                    {i.status === 'cambia' && <Text fontSize="sm">{schoolLabel(i.current)} → <b>{schoolLabel(i.school)}</b></Text>}
                    {i.status === 'igual' && <Text fontSize="sm" color="gray.600">{schoolLabel(i.school)}</Text>}
                    {i.reason && <Text fontSize="12px" color="orange.600">{i.reason}</Text>}
                  </Box>
                </Grid>
              ))}
              {items.length > SHOW && <Text fontSize="12px" color="gray.500" mt={2}>Mostrando {SHOW} de {items.length.toLocaleString('es-CO')}.</Text>}
            </>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          {parsed && <Button variant="ghost" onClick={() => (preview ? setPreview(null) : reset())} isDisabled={busy}>Atrás</Button>}
          {!parsed && <Button bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} onClick={read} isDisabled={!text.trim()}>Leer datos</Button>}
          {parsed && !preview && (
            <Button bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} onClick={runPreview} isLoading={busy} isDisabled={unmapped.length > 0}>
              {unmapped.length ? `Falta elegir ${unmapped.length} ${unmapped.length === 1 ? 'colegio' : 'colegios'}` : 'Ver vista previa'}
            </Button>
          )}
          {preview && (
            <Button bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} onClick={apply} isLoading={busy} isDisabled={!preview.summary.cambia}>
              Aplicar {preview.summary.cambia.toLocaleString('es-CO')} {preview.summary.cambia === 1 ? 'cambio' : 'cambios'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
